/**
 * Dashboard Page — Template Management
 *
 * Refactored: state lives in useDashboardState(), actions in useDashboardActions(),
 * header in DashboardHeader, toolbar in DashboardToolbar.
 * This file wires everything together.
 */
// @refresh reset

import { lazy, Suspense, useRef, useState as useStateReact, useEffect, startTransition, useCallback } from "react";
import zapLogo from "figma:asset/f3ea20a5ac1d9a14ff41e77e5c2314b781a6a6e5.png";
import {
  Loader2,
  Search,
  Plus,
  Sparkles,
  CheckSquare,
  Square,
  Archive,
  CloudOff,
  RefreshCw,
  Upload,
} from "lucide-react";

/* --- Hooks --- */
import { useDashboardState } from "../hooks/useDashboardState";
import { useDashboardActions } from "../hooks/useDashboardActions";
import { useDeadlineAlerts } from "../hooks/useDeadlineAlerts";

/* --- Eagerly loaded sub-components (needed for initial render) --- */
import { LoginScreen } from "../components/dashboard/LoginScreen";
import { DashboardHeader } from "../components/dashboard/DashboardHeader";
import { DashboardToolbar } from "../components/dashboard/DashboardToolbar";
import { TemplateCard } from "../components/dashboard/TemplateCard";
import { TemplateListRow } from "../components/dashboard/TemplateListRow";
import { PresenceAvatars } from "../components/shared/PresenceAvatars";
import { TemplateCardSkeleton, DashboardPageSkeleton } from "../components/shared/SkeletonLoading";

/* --- Lazy-loaded modals & panels (only imported on interaction) --- */
const CreateTemplateModal = lazy(() => import("../components/dashboard/CreateTemplateModal").then(m => ({ default: m.CreateTemplateModal })));
const ShareLinkModal = lazy(() => import("../components/dashboard/ShareLinkModal").then(m => ({ default: m.ShareLinkModal })));
const GlobalSearchModal = lazy(() => import("../components/shared/GlobalSearchModal").then(m => ({ default: m.GlobalSearchModal })));
const KeyboardShortcutsModal = lazy(() => import("../components/shared/KeyboardShortcutsModal").then(m => ({ default: m.KeyboardShortcutsModal })));
const CommandPalette = lazy(() => import("../components/shared/CommandPalette").then(m => ({ default: m.CommandPalette })));
const MasterTemplatesSection = lazy(() => import("../components/shared/MasterTemplatesSection").then(m => ({ default: m.MasterTemplatesSection })));
const UserManagementSection = lazy(() => import("../components/dashboard/UserManagement").then(m => ({ default: m.UserManagementSection })));
const BatchActionsBar = lazy(() => import("../components/dashboard/BatchActionsBar").then(m => ({ default: m.BatchActionsBar })));
const BulkAiGeneratorModal = lazy(() => import("../components/dashboard/BulkAiGeneratorModal").then(m => ({ default: m.BulkAiGeneratorModal })));
const FolderTagManager = lazy(() => import("../components/dashboard/FolderTagManager").then(m => ({ default: m.FolderTagManager })));
const WhiteLabelEditor = lazy(() => import("../components/dashboard/WhiteLabelEditor").then(m => ({ default: m.WhiteLabelEditor })));

// Deferred-mount wrapper: only renders children after initial paint,
// preventing lazy components from suspending during synchronous input.
function DeferredMount({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useStateReact(false);
  useEffect(() => {
    startTransition(() => setReady(true));
  }, []);
  return ready ? <>{children}</> : null;
}

/* ================================================================
   Dashboard Page
   ================================================================ */

export function DashboardPage() {
  const s = useDashboardState();
  const importFileRef = useRef<HTMLInputElement>(null);

  // Deadline approaching/overdue alerts (once per session)
  useDeadlineAlerts(s.accessibleTemplates, s.isReady);

  const actions = useDashboardActions({
    user: s.user,
    templates: s.templates,
    setTemplates: s.setTemplates,
    setActionLoading: s.setActionLoading,
    setDeleteConfirm: s.setDeleteConfirm,
    setEditingId: s.setEditingId,
    setShowCreateForm: s.setShowCreateForm,
    setShareInfo: s.setShareInfo,
    batchSelected: s.batchSelected,
    setBatchSelected: s.setBatchSelected,
    setBatchProcessing: s.setBatchProcessing,
    loadTemplates: s.loadTemplates,
  });

  // --- Auth guards ---
  if (!s.isReady) {
    return <DashboardPageSkeleton />;
  }

  if (!s.user) {
    try {
      const qp = new URLSearchParams(window.location.search);
      if (qp.has("cb") || qp.has("t") || qp.has("intake")) {
        return <DashboardPageSkeleton />;
      }
    } catch { /* ignore */ }
    // localStorage breadcrumb removed — the ?page= query param system
    // now handles shared link recovery without hijacking normal navigation.
    return <LoginScreen />;
  }

  return (
    <div dir="rtl" className="min-h-screen bg-[#f0f2f7] dark:bg-gray-900" style={{ fontFamily: "var(--font-family)" }}>
      {/* ---- Header ---- */}
      <DashboardHeader
        user={s.user}
        userAvatar={s.userAvatar}
        isDark={s.isDark}
        toggleTheme={s.toggleTheme}
        isAdmin={s.isAdmin}
        canCreateTemplates={s.canCreateTemplates}
        showUserMenu={s.showUserMenu}
        setShowUserMenu={s.setShowUserMenu}
        userMenuRef={s.userMenuRef}
        logout={s.logout}
        navigate={actions.navigate}
        activeTab={s.activeTab}
        setActiveTab={(tab) => startTransition(() => s.setActiveTab(tab))}
        showTabs={s.showTabs}
        showMastersTab={s.showMastersTab}
        showUsersTab={s.showUsersTab}
        showArchiveTab={s.showArchiveTab}
        stats={s.stats}
        archivedCount={s.archivedTemplates.length}
        onCreateTemplate={() => startTransition(() => s.setShowCreateForm(true))}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* ---- Stale data banner (offline fallback) ---- */}
        {s.isStaleData && (
          <div
            className="flex items-center gap-2 rounded-lg px-4 py-2.5 mb-4 text-sm"
            style={{
              backgroundColor: "#fef3c7",
              color: "#92400e",
              border: "1px solid #fcd34d",
            }}
          >
            <CloudOff className="w-4 h-4 flex-shrink-0" />
            <span>
              {"\u05DE\u05D5\u05E6\u05D2\u05D9\u05DD \u05E0\u05EA\u05D5\u05E0\u05D9\u05DD \u05E9\u05DE\u05D5\u05E8\u05D9\u05DD \u05DE\u05D4\u05DE\u05D7\u05E9\u05D1. \u05D7\u05DC\u05E7 \u05DE\u05D4\u05E9\u05D9\u05E0\u05D5\u05D9\u05D9\u05DD \u05E2\u05E9\u05D5\u05D9\u05D9\u05DD \u05DC\u05D0 \u05DC\u05D4\u05D9\u05D5\u05EA \u05DE\u05E2\u05D5\u05D3\u05DB\u05E0\u05D9\u05DD."}
            </span>
            <button
              type="button"
              className="btnReset flex items-center gap-1 mr-auto cursor-pointer underline text-xs"
              onClick={() => s.loadTemplates()}
            >
              <RefreshCw className="w-3 h-3" />
              {"\u05E0\u05E1\u05D4 \u05E9\u05D5\u05D1"}
            </button>
          </div>
        )}

        {/* ============ Templates Tab ============ */}
        {s.activeTab === "templates" && (
          <>
            {/* Import hidden input + Admin import button */}
            {s.isAdmin && (
              <>
                <input
                  ref={importFileRef}
                  type="file"
                  accept=".json,.zap.json"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      actions.handleImport(file);
                      e.target.value = "";
                    }
                  }}
                />
              </>
            )}

            {/* Toolbar */}
            {!s.isLoading && s.accessibleTemplates.length > 0 && (
              <>
                <DashboardToolbar
                  stats={s.stats}
                  filterType={s.filterType}
                  setFilterType={s.setFilterType}
                  filterFavoritesOnly={s.filterFavoritesOnly}
                  setFilterFavoritesOnly={s.setFilterFavoritesOnly}
                  favoritesCount={s.favorites.size}
                  selectedTags={s.selectedTags}
                  setSelectedTags={s.setSelectedTags}
                  allTags={s.allTags}
                  searchQuery={s.searchQuery}
                  setSearchQuery={s.setSearchQuery}
                  sortKey={s.sortKey}
                  setSortKey={s.setSortKey}
                  filterTeamMember={s.filterTeamMember}
                  setFilterTeamMember={s.setFilterTeamMember}
                  allTeamMembers={s.allTeamMembers}
                  filterStatus={s.filterStatus}
                  setFilterStatus={s.setFilterStatus}
                  filterFolder={s.filterFolder}
                  setFilterFolder={s.setFilterFolder}
                  folders={s.folders}
                  filterProjectType={s.filterProjectType}
                  setFilterProjectType={s.setFilterProjectType}
                  viewMode={s.viewMode}
                  setViewMode={s.setViewMode}
                  canEditTemplateMeta={s.canEditTemplateMeta}
                  onOpenFolderTagManager={() => startTransition(() => s.setShowFolderTagManager(true))}
                  templates={s.templates}
                />
              </>
            )}

            {/* Admin: Import template button */}
            {s.isAdmin && !s.isLoading && s.accessibleTemplates.length > 0 && (
              <div className="flex items-center justify-end mb-3 -mt-1">
                <button
                  type="button"
                  className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all"
                  onClick={() => importFileRef.current?.click()}
                  disabled={s.actionLoading === "import"}
                  title="ייבוא תבנית (.zap.json)"
                >
                  {s.actionLoading === "import" ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Upload className="w-3 h-3" />
                  )}
                  ייבוא
                </button>
              </div>
            )}

            {/* Loading — Skeleton UI instead of spinner */}
            {s.isLoading && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <TemplateCardSkeleton key={i} />
                ))}
              </div>
            )}

            {/* Empty state */}
            {!s.isLoading && s.accessibleTemplates.length === 0 && (
              <div className="text-center py-24">
                <div className="relative inline-block mb-6">
                  <div className="w-24 h-24 bg-zap-pale rounded-3xl flex items-center justify-center mx-auto shadow-sm">
                    <img src={zapLogo} alt="Zap Group" className="w-14 h-14 rounded-xl" />
                  </div>
                  <div className="absolute -top-1 -left-1 w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-md">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                </div>
                <h2 className="text-xl text-gray-800 mb-2" style={{ fontWeight: 700 }}>
                  {s.canCreateTemplates ? "\u05D1\u05E8\u05D5\u05DB\u05D9\u05DD \u05D4\u05D1\u05D0\u05D9\u05DD!" : "\u05D0\u05D9\u05DF \u05EA\u05D1\u05E0\u05D9\u05D5\u05EA \u05D6\u05DE\u05D9\u05E0\u05D5\u05EA"}
                </h2>
                <p className="text-gray-400 mb-8 text-sm max-w-sm mx-auto">
                  {s.canCreateTemplates
                    ? "\u05E6\u05E8\u05D5 \u05D0\u05EA \u05D4\u05EA\u05D1\u05E0\u05D9\u05D5\u05EA \u05D4\u05E8\u05D0\u05E9\u05D5\u05E0\u05D4 \u05E9\u05DC\u05DB\u05DD \u05DB\u05D3\u05D9 \u05DC\u05D4\u05EA\u05D7\u05D9\u05DC"
                    : "\u05E4\u05E0\u05D5 \u05DC\u05DE\u05E0\u05D4\u05DC \u05D4\u05DE\u05E2\u05E8\u05DB\u05EA \u05DC\u05E7\u05D1\u05DC\u05EA \u05D2\u05D9\u05E9\u05D4 \u05DC\u05EA\u05D1\u05E0\u05D9\u05D5\u05EA"}
                </p>
                {s.canCreateTemplates && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => startTransition(() => s.setShowCreateForm(true))}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") startTransition(() => s.setShowCreateForm(true)); }}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-zap text-white rounded-xl hover:bg-zap-navy transition-all text-sm cursor-pointer shadow-lg shadow-zap/20"
                    style={{ fontWeight: 600 }}
                  >
                    <Plus className="w-4 h-4" />
                    {"\u05E6\u05E8\u05D5 \u05EA\u05D1\u05E0\u05D9\u05D5\u05EA \u05D7\u05D3\u05E9\u05D4"}
                  </span>
                )}
              </div>
            )}

            {/* Grid view */}
            {!s.isLoading && s.sortedAndFiltered.length > 0 && s.viewMode === "grid" && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {s.sortedAndFiltered.map((t) => {
                  const tPresence = s.presenceMap.get(t.id) || [];
                  const isBatchSelected = s.batchSelected.has(t.id);
                  return (
                    <div key={t.id} className="relative h-full">
                      {tPresence.length > 0 && (
                        <div className="absolute top-2 left-2 z-10">
                          <PresenceAvatars users={tPresence} />
                        </div>
                      )}
                      {s.canDeleteTemplates && (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); actions.toggleBatchSelect(t.id); }}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); actions.toggleBatchSelect(t.id); } }}
                          className={`absolute top-2 right-2 z-10 w-6 h-6 rounded-md flex items-center justify-center cursor-pointer ${
                            isBatchSelected
                              ? "bg-zap text-white shadow-sm"
                              : "bg-white/80 text-gray-300 opacity-0 group-hover:opacity-100 hover:text-zap hover:bg-white shadow-sm border border-gray-200"
                          }`}
                          style={{ transition: "opacity 0.15s" }}
                          title={isBatchSelected ? "\u05D1\u05D8\u05DC \u05D1\u05D7\u05D9\u05E8\u05D4" : "\u05D1\u05D7\u05E8 \u05DC\u05E4\u05E2\u05D5\u05DC\u05D4 \u05DE\u05E8\u05D5\u05DB\u05D6\u05EA"}
                        >
                          {isBatchSelected ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                        </span>
                      )}
                      <TemplateCard
                        template={t}
                        isEditing={s.editingId === t.id}
                        isDeleting={s.deleteConfirm === t.id}
                        actionLoading={s.actionLoading}
                        isAdmin={s.isAdmin}
                        canEditMeta={s.canEditTemplateMeta}
                        canDelete={s.canDeleteTemplates}
                        canDuplicate={s.canCreateTemplates}
                        allTags={s.allTags}
                        allUsers={s.allUsers}
                        onOpen={() => actions.navigate(`/t/${t.id}/`)}
                        onEdit={() => s.setEditingId(s.editingId === t.id ? null : t.id)}
                        onSaveEdit={(updates) => actions.handleUpdate(t.id, updates)}
                        onCancelEdit={() => s.setEditingId(null)}
                        onDuplicate={() => actions.handleDuplicate(t)}
                        onDelete={() => {
                          if (s.deleteConfirm === t.id) {
                            actions.handleDelete(t.id);
                          } else {
                            s.setDeleteConfirm(t.id);
                          }
                        }}
                        onCancelDelete={() => s.setDeleteConfirm(null)}
                        onShare={() => startTransition(() => actions.handleShare(t.id))}
                        onFavorite={() => s.toggleFavorite(t.id)}
                        isFavorite={s.favorites.has(t.id)}
                        commentCount={s.commentStats[t.id]?.total}
                        onArchive={s.canEditTemplateMeta ? () => actions.handleArchive(t.id) : undefined}
                        allFolders={s.folders}
                        onWhiteLabel={() => startTransition(() => s.setWhiteLabelTemplate(t))}
                        onExport={s.isAdmin ? () => actions.handleExport(t.id) : undefined}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {/* List view */}
            {!s.isLoading && s.sortedAndFiltered.length > 0 && s.viewMode === "list" && (
              <div className="bg-white rounded-xl border border-gray-200/80 shadow-sm overflow-x-auto">
                <div className="grid grid-cols-[minmax(160px,1fr)_70px_60px_minmax(80px,1fr)_80px_minmax(60px,100px)_70px_70px_auto] items-center gap-3 px-4 py-2.5 bg-gray-50 border-b border-gray-100 text-[11px] text-gray-400 min-w-[860px]" style={{ fontWeight: 600 }}>
                  <span>{"\u05E9\u05DD \u05DC\u05E7\u05D5\u05D7"}</span>
                  <span>{"\u05E1\u05D5\u05D2"}</span>
                  <span>{"\u05E9\u05DC\u05D1"}</span>
                  <span>{"\u05E6\u05D5\u05D5\u05EA"}</span>
                  <span>{"\u05D0\u05D7\u05E8\u05D9\u05D5\u05EA"}</span>
                  <span>{"\u05EA\u05D2\u05D9\u05D5\u05EA"}</span>
                  <span>{"\u05E0\u05D5\u05E6\u05E8"}</span>
                  <span>{"\u05E2\u05D5\u05D3\u05DB\u05DF"}</span>
                  <span>{"\u05E4\u05E2\u05D5\u05DC\u05D5\u05EA"}</span>
                </div>
                {s.sortedAndFiltered.map((t) => (
                  <TemplateListRow
                    key={t.id}
                    template={t}
                    actionLoading={s.actionLoading}
                    isAdmin={s.isAdmin}
                    canDelete={s.canDeleteTemplates}
                    canDuplicate={s.canCreateTemplates}
                    allUsers={s.allUsers}
                    onOpen={() => actions.navigate(`/t/${t.id}/`)}
                    onDuplicate={() => actions.handleDuplicate(t)}
                    onDelete={() => {
                      if (s.deleteConfirm === t.id) {
                        actions.handleDelete(t.id);
                      } else {
                        s.setDeleteConfirm(t.id);
                      }
                    }}
                    isDeleting={s.deleteConfirm === t.id}
                    onCancelDelete={() => s.setDeleteConfirm(null)}
                    onShare={() => startTransition(() => actions.handleShare(t.id))}
                    onFavorite={() => s.toggleFavorite(t.id)}
                    isFavorite={s.favorites.has(t.id)}
                  />
                ))}
              </div>
            )}

            {/* No results */}
            {!s.isLoading && s.accessibleTemplates.length > 0 && s.sortedAndFiltered.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-gray-300" />
                </div>
                <p className="text-gray-400 text-sm">
                  {"\u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0\u05D5 \u05EA\u05D1\u05E0\u05D9\u05D5\u05EA \u05DC\u05D7\u05D9\u05E4\u05D5\u05E9 \u201C"}{s.searchQuery}{"\u201D"}
                </p>
              </div>
            )}
          </>
        )}

        {/* ============ Masters Tab ============ */}
        {s.activeTab === "masters" && s.showMastersTab && (
          <Suspense fallback={<div className="text-center py-24"><Loader2 className="w-10 h-10 text-gray-300" /></div>}>
            <MasterTemplatesSection
              allTemplates={s.templates}
              onTemplatesChanged={s.loadTemplates}
            />
          </Suspense>
        )}

        {/* ============ Users Tab ============ */}
        {s.activeTab === "users" && s.showUsersTab && (
          <Suspense fallback={<div className="text-center py-24"><Loader2 className="w-10 h-10 text-gray-300" /></div>}>
            <UserManagementSection templates={s.templates} onUsersChanged={s.loadUsers} />
          </Suspense>
        )}

        {/* ============ Archive Tab ============ */}
        {s.activeTab === "archive" && s.showArchiveTab && (
          <div>
            {s.archivedTemplates.length === 0 ? (
              <div className="text-center py-24">
                <div className="w-20 h-20 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-4">
                  <Archive className="w-10 h-10 text-gray-300" />
                </div>
                <h2 className="text-lg text-gray-600 mb-2" style={{ fontWeight: 700 }}>
                  {"\u05D4\u05D0\u05E8\u05DB\u05D9\u05D5\u05DF \u05E8\u05D9\u05E7"}
                </h2>
                <p className="text-sm text-gray-400 max-w-sm mx-auto">
                  {"\u05EA\u05D1\u05E0\u05D9\u05D5\u05EA \u05E9\u05D9\u05D5\u05E2\u05D1\u05E8\u05D5 \u05DC\u05D0\u05E8\u05DB\u05D9\u05D5\u05DF \u05D9\u05D5\u05E4\u05D9\u05E2\u05D5 \u05DB\u05D0\u05DF. \u05D0\u05E4\u05E9\u05E8 \u05DC\u05E9\u05D7\u05D6\u05E8 \u05D0\u05D5 \u05DC\u05DE\u05D7\u05D5\u05E7 \u05DC\u05E6\u05DE\u05D9\u05EA\u05D5\u05EA."}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Archive className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-500" style={{ fontWeight: 600 }}>
                    {s.archivedTemplates.length}{" \u05EA\u05D1\u05E0\u05D9\u05D5\u05EA \u05D1\u05D0\u05E8\u05DB\u05D9\u05D5\u05DF"}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {s.archivedTemplates.map((t) => (
                    <div key={t.id} className="relative opacity-75 hover:opacity-100" style={{ transition: "opacity 0.2s" }}>
                      <TemplateCard
                        template={t}
                        isEditing={false}
                        isDeleting={s.deleteConfirm === t.id}
                        actionLoading={s.actionLoading}
                        isAdmin={s.isAdmin}
                        canEditMeta={s.canEditTemplateMeta}
                        canDelete={s.canDeleteTemplates}
                        canDuplicate={s.canCreateTemplates}
                        allTags={s.allTags}
                        allUsers={s.allUsers}
                        onOpen={() => actions.navigate(`/t/${t.id}/`)}
                        onEdit={() => {}}
                        onSaveEdit={() => {}}
                        onCancelEdit={() => {}}
                        onDuplicate={() => actions.handleDuplicate(t)}
                        onDelete={() => {
                          if (s.deleteConfirm === t.id) {
                            actions.handleDelete(t.id);
                          } else {
                            s.setDeleteConfirm(t.id);
                          }
                        }}
                        onCancelDelete={() => s.setDeleteConfirm(null)}
                        onShare={() => actions.handleShare(t.id)}
                        isArchiveMode
                        onRestore={() => actions.handleRestore(t.id)}
                        allFolders={s.folders}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* ---- Modals ---- */}
      {s.showCreateForm && (
        <Suspense fallback={<div className="text-center py-24"><Loader2 className="w-10 h-10 text-gray-300" /></div>}>
          <CreateTemplateModal
            isLoading={s.actionLoading === "create"}
            masterTemplates={s.masterTemplates}
            allTags={s.allTags}
            allUsers={s.allUsers}
            onSubmit={actions.handleCreate}
            onClose={() => s.setShowCreateForm(false)}
          />
        </Suspense>
      )}

      {s.shareInfo && (
        <Suspense fallback={<div className="text-center py-24"><Loader2 className="w-10 h-10 text-gray-300" /></div>}>
          <ShareLinkModal
            templateId={s.shareInfo.id}
            onClose={() => s.setShareInfo(null)}
          />
        </Suspense>
      )}

      <DeferredMount>
        <Suspense fallback={null}>
          <CommandPalette templates={s.templates} dashboardMode />
        </Suspense>
        <Suspense fallback={null}>
          <KeyboardShortcutsModal />
        </Suspense>
      </DeferredMount>
      {s.showGlobalSearch && (
        <Suspense fallback={null}>
          <GlobalSearchModal open={s.showGlobalSearch} onClose={() => startTransition(() => s.setShowGlobalSearch(false))} />
        </Suspense>
      )}

      {s.canDeleteTemplates && s.batchSelected.size > 0 && (
        <Suspense fallback={null}>
          <BatchActionsBar
            selectedCount={s.batchSelected.size}
            onClearSelection={actions.clearBatchSelection}
            onBatchDelete={actions.handleBatchDelete}
            onBatchStatusChange={actions.handleBatchStatusChange}
            onBatchAddTag={actions.handleBatchAddTag}
            onBulkAiGenerate={() => startTransition(() => s.setShowBulkAiModal(true))}
            allTags={s.allTags}
            isProcessing={s.batchProcessing}
          />
        </Suspense>
      )}

      {s.showBulkAiModal && (
        <Suspense fallback={null}>
          <BulkAiGeneratorModal
            isOpen={s.showBulkAiModal}
            onClose={() => s.setShowBulkAiModal(false)}
            templates={s.templates.filter((t) => s.batchSelected.has(t.id))}
            onComplete={() => {
              s.loadTemplates();
              actions.clearBatchSelection();
            }}
          />
        </Suspense>
      )}

      {s.showFolderTagManager && (
        <Suspense fallback={null}>
          <FolderTagManager
            isOpen={s.showFolderTagManager}
            onClose={() => s.setShowFolderTagManager(false)}
            onDataChanged={s.loadFoldersAndTags}
            existingTags={s.allTags}
          />
        </Suspense>
      )}

      {s.whiteLabelTemplate && (
        <Suspense fallback={<div className="text-center py-24"><Loader2 className="w-10 h-10 text-gray-300" /></div>}>
          <WhiteLabelEditor
            isOpen={!!s.whiteLabelTemplate}
            onClose={() => s.setWhiteLabelTemplate(null)}
            template={s.whiteLabelTemplate}
            onSaved={(updated) => {
              s.setTemplates((prev) => prev.map((t) => t.id === updated.id ? updated : t));
            }}
          />
        </Suspense>
      )}
    </div>
  );
}

export default DashboardPage;